// Tap-to-pay marketing callout for per-trade pages.
//
// Pitch: the trader's phone IS the card reader. Stripe Tap to Pay
// runs on iPhone (US/UK/select EU) and Google's equivalent on
// Android, so the marketing copy stays platform-agnostic ("your
// phone", not "iPhone"). Customer taps their card or phone, payment
// lands in the trader's account, no terminal to buy, no "I'll
// invoice you next week" chase. The differentiator vs traditional
// trade SaaS is (a) zero hardware (no SumUp / iZettle reader to
// lose), (b) the tradesperson takes payment at the customer's
// doorstep / van side / chair / treatment room without changing
// posture.
//
// Visual is intentionally illustrative, not a literal phone slab --
// two stylised phones at opposing tilts with a contactless arc between
// them, on a soft accent wash. Reference: pay.nl iPhone-Pay marketing
// shot Barry pinned. We don't try to be photo-realistic; we go for
// the modern flat-illustration vibe that Stripe / Square / Adyen use
// on their tap-to-pay pages.
import * as React from "react";
import { CheckCircle, Lightning } from "@phosphor-icons/react";
import { FadeInOnScroll } from "@/components/ui/FadeInOnScroll";

export type TapToPayAccent =
  | "amber"
  | "blue"
  | "green"
  | "rose"
  | "violet"
  | "teal"
  | "orange";

export interface TapToPayCalloutProps {
  /** Trade-page accent so the section sits in the same colour family
   *  as the rest of the trade page. Default amber so the brand carries
   *  when no accent is supplied. */
  accent?: TapToPayAccent;
  /** Plausible per-job ticket displayed on the mock terminal screen.
   *  Defaults to "£85.00" -- per-trade callers should pass their own
   *  representative figure (a hair stylist's average vs a locksmith
   *  call-out are very different bills). */
  ticketDisplay?: string;
  /** Short label rendered on the mock terminal under "Pay". Defaults
   *  to "Job complete -- tap to pay". Per-trade callers can pass
   *  trade-flavoured text ("Service done", "Cut & blow-dry done",
   *  etc.). */
  ticketLabel?: string;
  /** Which side the illustration sits on. Defaults to "right" (copy
   *  on the left). Per-trade pages alternate left/right between hero
   *  + sub-brand sections to give the page a left-right-left rhythm
   *  rather than three image-right blocks in a row. */
  imageSide?: "left" | "right";
}

// Dark accent surfaces -- mirrors CashCallout + HouseProud so the
// three-callout band reads dark/dark/dark instead of light/light/dark.
const SECTION_BG: Record<TapToPayAccent, string> = {
  amber: "bg-gradient-to-br from-amber-900 to-amber-800",
  rose: "bg-gradient-to-br from-rose-900 to-rose-800",
  violet: "bg-gradient-to-br from-violet-900 to-violet-800",
  teal: "bg-gradient-to-br from-teal-900 to-teal-800",
  green: "bg-gradient-to-br from-green-900 to-green-800",
  blue: "bg-gradient-to-br from-blue-900 to-blue-800",
  orange: "bg-gradient-to-br from-orange-900 to-orange-800",
};

const ACCENT_TEXT: Record<TapToPayAccent, string> = {
  amber: "text-amber-200",
  rose: "text-rose-200",
  violet: "text-violet-200",
  teal: "text-teal-200",
  green: "text-green-200",
  blue: "text-blue-200",
  orange: "text-orange-200",
};

const ACCENT_TEXT_LIGHT: Record<TapToPayAccent, string> = {
  amber: "text-amber-300",
  rose: "text-rose-300",
  violet: "text-violet-300",
  teal: "text-teal-300",
  green: "text-green-300",
  blue: "text-blue-300",
  orange: "text-orange-300",
};

// Accent fills inside the SVG illustration. These render against
// LIGHT phone surfaces (stone-100 phone body + white screen, locked
// regardless of mode), so they stay at their light-mode values in
// both colour modes -- no dark-mode flip.
const ACCENT_BG_FILL: Record<TapToPayAccent, string> = {
  amber: "fill-amber-500",
  rose: "fill-rose-500",
  violet: "fill-violet-500",
  teal: "fill-teal-500",
  green: "fill-green-500",
  blue: "fill-blue-500",
  orange: "fill-orange-500",
};

const ACCENT_BG_STROKE: Record<TapToPayAccent, string> = {
  amber: "stroke-amber-500",
  rose: "stroke-rose-500",
  violet: "stroke-violet-500",
  teal: "stroke-teal-500",
  green: "stroke-green-500",
  blue: "stroke-blue-500",
  orange: "stroke-orange-500",
};

const ACCENT_PILL: Record<TapToPayAccent, string> = {
  amber: "bg-white/10 text-amber-100 border-white/20",
  rose: "bg-white/10 text-rose-100 border-white/20",
  violet: "bg-white/10 text-violet-100 border-white/20",
  teal: "bg-white/10 text-teal-100 border-white/20",
  green: "bg-white/10 text-green-100 border-white/20",
  blue: "bg-white/10 text-blue-100 border-white/20",
  orange: "bg-white/10 text-orange-100 border-white/20",
};

const BLUR_TINT: Record<TapToPayAccent, string> = {
  amber: "bg-amber-400 opacity-20",
  rose: "bg-rose-400 opacity-20",
  violet: "bg-violet-400 opacity-20",
  teal: "bg-teal-400 opacity-20",
  green: "bg-green-400 opacity-20",
  blue: "bg-blue-400 opacity-20",
  orange: "bg-orange-400 opacity-20",
};

export function TapToPayCallout({
  accent = "amber",
  ticketDisplay = "£85.00",
  ticketLabel = "Job complete -- tap to pay",
  imageSide = "right",
}: TapToPayCalloutProps) {
  return (
    <FadeInOnScroll>
      <section
        className={`py-24 ${SECTION_BG[accent]} text-white relative overflow-hidden`}
      >
        {/* Background blur for depth -- soft accent glow on dark */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className={`absolute -top-32 right-1/4 w-96 h-96 rounded-full blur-3xl ${BLUR_TINT[accent]}`}
          />
          <div
            className={`absolute -bottom-32 -left-24 w-80 h-80 rounded-full blur-3xl ${BLUR_TINT[accent]}`}
          />
        </div>

        <div className="relative mx-auto max-w-6xl px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Copy column. The order-* classes flip the column when
                imageSide="left" so the page reads with the trade-page
                hero (image right) -> TapToPay (image left) -> next
                sub-brand block (image right) alternation. */}
            <div
              className={`space-y-6 ${
                imageSide === "left" ? "lg:order-2" : "lg:order-1"
              }`}
            >
              <div
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${ACCENT_PILL[accent]}`}
              >
                <Lightning
                  className={`h-4 w-4 ${ACCENT_TEXT_LIGHT[accent]}`}
                  weight="fill"
                />
                <span className="text-xs font-semibold uppercase tracking-wider">
                  Tap to pay on your phone
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight text-white">
                Get paid before you leave the
                <br />
                <span className={ACCENT_TEXT[accent]}>doorstep.</span>
              </h2>
              <p className="text-base text-white/80 leading-relaxed max-w-md">
                Your phone IS the card reader. Customer taps their card
                or phone, payment lands in seconds. No terminal to buy,
                no &quot;I&apos;ll send the invoice and we&apos;ll see&quot; chase, no
                waiting three weeks for a bank transfer.
              </p>
              <ul className="space-y-3">
                {[
                  "Zero hardware -- just your phone",
                  "Cards, Apple Pay, Google Pay -- all tap-to-pay",
                  "Funds settle to your bank, not a separate wallet",
                  "Falls back to a payment link if the customer can't tap",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-sm text-white/90"
                  >
                    <CheckCircle
                      className={`h-5 w-5 ${ACCENT_TEXT_LIGHT[accent]} shrink-0 mt-0.5`}
                      weight="fill"
                    />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Illustration column. order-1 when imageSide="left" so
                it lands on the actual left of the grid; order-2 (the
                default) when imageSide="right". */}
            <div
              className={`hidden lg:flex justify-center ${
                imageSide === "left" ? "lg:order-1" : "lg:order-2"
              }`}
            >
              <TapIllustration
                accent={accent}
                ticketDisplay={ticketDisplay}
                ticketLabel={ticketLabel}
              />
            </div>
          </div>
        </div>
      </section>
    </FadeInOnScroll>
  );
}

interface IllustrationProps {
  accent: TapToPayAccent;
  ticketDisplay: string;
  ticketLabel: string;
}

// SVG-based illustrative composition: two phones at opposing tilts
// with a contactless arc between them. Stylised, not realistic --
// thin strokes, flat fills, accent-tinted highlights. Sized to the
// 480x420 viewBox so it scales cleanly inside the right column.
function TapIllustration({
  accent,
  ticketDisplay,
  ticketLabel,
}: IllustrationProps) {
  return (
    <div className="relative w-full max-w-[460px] aspect-[480/420]">
      <svg
        viewBox="0 0 480 420"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full"
        aria-hidden="true"
      >
        {/* Soft floor shadow */}
        <ellipse
          cx="240"
          cy="370"
          rx="170"
          ry="10"
          className="fill-stone-300/30 dark:fill-stone-700/20"
        />

        {/* Trader's phone -- tilted left, shows the "Tap to pay"
            terminal screen with the amount + contactless icon. Body
            uses rounded-3xl-equivalent corners (rx=24) and the
            stone-200 light bezel that matches the rest of the site
            (cards / inputs all sit at rounded-md/lg with thin
            stone-200 borders). The previous heavy black bezel read
            as "iPhone marketing render"; this is "Manos illustration". */}
        <g transform="translate(60, 60) rotate(-10 120 175)">
          <rect
            x="0"
            y="0"
            width="180"
            height="300"
            rx="24"
            className="fill-stone-100 stroke-stone-200"
            strokeWidth="2"
          />
          <rect
            x="8"
            y="8"
            width="164"
            height="284"
            rx="16"
            className="fill-white"
          />

          {/* Tap-to-pay pill */}
          <g transform="translate(40, 36)">
            <rect
              width="100"
              height="20"
              rx="10"
              className={ACCENT_BG_FILL[accent]}
              opacity="0.15"
            />
            <text
              x="50"
              y="14"
              textAnchor="middle"
              className={ACCENT_TEXT_LIGHT[accent]}
              style={{ font: "600 10px system-ui, sans-serif" }}
            >
              Tap to pay
            </text>
          </g>

          {/* Amount */}
          <text
            x="90"
            y="120"
            textAnchor="middle"
            className="fill-stone-900"
            style={{ font: "800 32px system-ui, sans-serif" }}
          >
            {ticketDisplay}
          </text>
          <text
            x="90"
            y="142"
            textAnchor="middle"
            className="fill-stone-500"
            style={{ font: "500 10px system-ui, sans-serif" }}
          >
            {ticketLabel.length > 26
              ? ticketLabel.slice(0, 24) + "..."
              : ticketLabel}
          </text>

          {/* Contactless waves + accent halo */}
          <g transform="translate(50, 180)">
            <circle
              cx="40"
              cy="40"
              r="34"
              className={ACCENT_BG_FILL[accent]}
              opacity="0.12"
            />
            <path
              d="M 32 30 Q 44 40 32 50"
              fill="none"
              strokeWidth="2.5"
              strokeLinecap="round"
              className={ACCENT_BG_STROKE[accent]}
            />
            <path
              d="M 40 24 Q 58 40 40 56"
              fill="none"
              strokeWidth="2.5"
              strokeLinecap="round"
              className={ACCENT_BG_STROKE[accent]}
            />
            <path
              d="M 48 18 Q 70 40 48 62"
              fill="none"
              strokeWidth="2.5"
              strokeLinecap="round"
              className={ACCENT_BG_STROKE[accent]}
            />
          </g>

          <text
            x="90"
            y="270"
            textAnchor="middle"
            className="fill-stone-400"
            style={{ font: "500 9px system-ui, sans-serif" }}
          >
            Hold a card near
          </text>
        </g>

        {/* Customer's phone -- tilted right, shows "Approved" */}
        <g transform="translate(250, 100) rotate(12 90 140)">
          <rect
            x="0"
            y="0"
            width="160"
            height="270"
            rx="24"
            className="fill-stone-100 stroke-stone-200"
            strokeWidth="2"
          />
          <rect
            x="8"
            y="8"
            width="144"
            height="254"
            rx="16"
            className="fill-white"
          />

          {/* Card surface (no detailed numbers -- just shape + chip) */}
          <g transform="translate(20, 36)">
            <rect
              width="120"
              height="68"
              rx="10"
              className={ACCENT_BG_FILL[accent]}
            />
            <rect
              x="12"
              y="18"
              width="20"
              height="14"
              rx="2"
              className="fill-white"
              opacity="0.85"
            />
            <path
              d="M 78 28 Q 84 34 78 40"
              fill="none"
              strokeWidth="2"
              strokeLinecap="round"
              className="stroke-white"
              opacity="0.7"
            />
            <path
              d="M 82 22 Q 92 34 82 46"
              fill="none"
              strokeWidth="2"
              strokeLinecap="round"
              className="stroke-white"
              opacity="0.5"
            />
          </g>

          {/* Approved row */}
          <g transform="translate(20, 130)">
            <rect
              width="120"
              height="32"
              rx="6"
              className="fill-emerald-50"
            />
            <circle cx="20" cy="16" r="8" className="fill-emerald-500" />
            <path
              d="M 16 16 L 19 19 L 25 13"
              fill="none"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="stroke-white"
            />
            <text
              x="38"
              y="20"
              className="fill-emerald-700"
              style={{ font: "700 10px system-ui, sans-serif" }}
            >
              Approved
            </text>
          </g>

          <text
            x="80"
            y="195"
            textAnchor="middle"
            className="fill-stone-900"
            style={{ font: "800 20px system-ui, sans-serif" }}
          >
            {ticketDisplay}
          </text>
          <text
            x="80"
            y="215"
            textAnchor="middle"
            className="fill-stone-500"
            style={{ font: "500 9px system-ui, sans-serif" }}
          >
            paid just now
          </text>
        </g>

        {/* Contactless ripple at the tap point -- two thin circles
            mark where the phones meet. Quieter than the previous
            three-ring composition. */}
        <g transform="translate(245, 170)">
          <circle
            cx="0"
            cy="0"
            r="44"
            fill="none"
            strokeWidth="2"
            strokeDasharray="3 5"
            strokeLinecap="round"
            className={ACCENT_BG_STROKE[accent]}
            opacity="0.5"
          />
          <circle
            cx="0"
            cy="0"
            r="22"
            fill="none"
            strokeWidth="2"
            strokeLinecap="round"
            className={ACCENT_BG_STROKE[accent]}
          />
          <circle
            cx="0"
            cy="0"
            r="5"
            className={ACCENT_BG_FILL[accent]}
          />
        </g>
      </svg>
    </div>
  );
}

